import catalog from '../../shared/adminPermissionsCatalog.json';

export type AdminPermissionNode = {
  code: string;
  label: string;
  description?: string;
  group?: string;
  children?: AdminPermissionNode[];
};

export const ADMIN_PERMISSION_TREE = (catalog.tree || []) as AdminPermissionNode[];

/** 首页指标字段 → 所需叶子权限（与 shared/adminPermissionsCatalog.json 对齐） */
export const DASHBOARD_METRIC_PERMISSIONS = (catalog.dashboardMetricPermissions || {}) as {
  verifiedCreators?: string;
  totalAlbums?: string;
  platformRevenue?: string;
};

function flattenLeafCodes(nodes: AdminPermissionNode[], out: string[] = []): string[] {
  for (const node of nodes) {
    if (node.children && node.children.length > 0) {
      flattenLeafCodes(node.children, out);
    } else if (node.code) {
      out.push(node.code);
    }
  }
  return out;
}

export const ALL_ADMIN_PERMISSION_CODES = flattenLeafCodes(ADMIN_PERMISSION_TREE);

export function isAdminLikeRole(role?: string | null): boolean {
  return role === 'admin' || role === 'super_admin';
}

export function isSuperAdminRole(role?: string | null): boolean {
  return role === 'super_admin';
}

export function collectLeafCodes(node: AdminPermissionNode): string[] {
  if (!node.children || node.children.length === 0) return node.code ? [node.code] : [];
  return node.children.flatMap(collectLeafCodes);
}

/** 是否拥有某权限（超管恒 true；支持父级码：任一叶子命中即 true） */
export function canAccess(
  role: string | null | undefined,
  permissions: string[] | null | undefined,
  code: string | string[]
): boolean {
  if (isSuperAdminRole(role)) return true;
  if (!isAdminLikeRole(role)) return false;
  const set = new Set(permissions || []);
  const codes = Array.isArray(code) ? code : [code];
  return codes.some((c) => {
    if (set.has(c)) return true;
    const node = findNode(ADMIN_PERMISSION_TREE, c);
    if (!node) return false;
    const leaves = collectLeafCodes(node);
    return leaves.some((leaf) => set.has(leaf));
  });
}

function findNode(nodes: AdminPermissionNode[], code: string): AdminPermissionNode | null {
  for (const n of nodes) {
    if (n.code === code) return n;
    if (n.children) {
      const found = findNode(n.children, code);
      if (found) return found;
    }
  }
  return null;
}

/** 设置页 mode → 权限码（copyright-cases 复用版权认证权限） */
export const SETTINGS_MODE_PERMISSION: Record<string, string> = {
  'exchange-rate': 'settings.exchange_rate',
  'copyright-price': 'settings.copyright_price',
  'copyright-cases': 'copyright.registration',
  'optimization-keep-price': 'settings.optimization_keep_price',
  'subscription-plans': 'settings.subscription_plans',
  'redemption-codes': 'settings.redemption_codes',
  'channel-promotions': 'settings.channel_promotions',
  'platform-management': 'settings.platform_management',
  'wechat-config': 'settings.wechat_config',
  'agreement-management': 'settings.agreement_management',
  'help-center': 'settings.help_center',
  'feedback-management': 'settings.feedback_management',
  'collaborator-invites': 'settings.collaborator_invites',
  'email-logs': 'settings.email_logs',
  'feature-modules': 'settings.feature_modules',
  'notification-settings': 'settings.notification_settings',
  'online-users': 'settings.online_users',
  'login-logs': 'settings.login_logs',
  'admin-permissions': '__super_admin_only__'
};

export const REVENUE_MODE_PERMISSION: Record<string, string> = {
  'share-ratios': 'revenue.share_ratios',
  allocations: 'revenue.allocations',
  'confirmed-allocations': 'revenue.allocations',
  'creator-view': 'revenue.creator_view',
  withdrawals: 'revenue.withdrawals',
  'tax-filings': 'revenue.tax_filings'
};

export const MEMBERSHIP_MODE_PERMISSION: Record<string, string> = {
  'user-subscriptions': 'membership.subscriptions',
  orders: 'membership.orders'
};

export const DATA_MODE_PERMISSION: Record<string, string> = {
  upload: 'data.import',
  creators: 'data.query'
};

export const AI_BROKER_MODE_PERMISSION: Record<string, string> = {
  users: 'ai_broker.users',
  conversations: 'ai_broker.conversations',
  stats: 'ai_broker.stats',
  chat: 'ai_broker.chat'
};

export const OPTIMIZATION_OPT_PERMISSION: Record<string, string> = {
  list: 'works.optimization.publish',
  status: 'works.optimization.progress',
  cleanup: 'works.optimization.cleanup',
  'creator-preview': 'works.optimization.progress',
  'creator-mirror': 'works.optimization.progress'
};
